/**
 * UnicodeFont.js
 * Fetches and caches a CJK-capable font (Noto Sans SC) for use with jsPDF.
 * This allows jsPDF + autoTable to render Chinese, Japanese, Korean,
 * and other Unicode characters correctly.
 */

import notoSansSCUrl from "../../assets/fonts/NotoSansSC-Regular.ttf";

// Vite emits this imported TTF as a versioned application asset. It does not
// use the public-folder base path or any external CDN.
const FONT_URL = notoSansSCUrl;

let cachedBase64 = null;

/**
 * Fetches the Unicode font and returns the Base64 string.
 * Caches the result so the font is only downloaded once per session.
 */
export async function getUnicodeFontBase64() {
    if (cachedBase64) return cachedBase64;

    const response = await fetch(FONT_URL);
    if (!response.ok) {
        throw new Error(`Failed to load local Chinese PDF font: ${ response.status }`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCodePoint(...bytes.subarray(i, i + chunkSize));
    }
    cachedBase64 = btoa(binary);
    return cachedBase64;
}

/**
 * Registers the Unicode font with the given jsPDF instance.
 * After calling this, use: pdf.setFont("NotoSansSC", "normal")
 */
export async function registerUnicodeFont(pdf) {
    const base64 = await getUnicodeFontBase64();
    pdf.addFileToVFS("NotoSansSC.ttf", base64);
    pdf.addFont("NotoSansSC.ttf", "NotoSansSC", "normal");
}